import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaImages,
} from "react-icons/fa";

const PhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const photosPerPage = 20;

  // =====================================================
  // LOAD PHOTOS
  // =====================================================

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);

        const q = query(
          collection(db, "photos"),
          orderBy("timestamp", "desc")
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPhotos(data);
      } catch (error) {
        console.error("Photo load error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // =====================================================
  // LOAD CATEGORIES
  // =====================================================

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "categories")
        );

        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort(
            (a, b) =>
              Number(a.order || 0) -
              Number(b.order || 0)
          );

        setCategories(data);
      } catch (error) {
        console.error("Category load error:", error);
      }
    };

    fetchCategories();
  }, []);

  // =====================================================
  // CATEGORY FILTER
  // =====================================================

  const filteredPhotos =
    activeCategory === "all"
      ? photos
      : photos.filter(
          (photo) =>
            photo.category === activeCategory
        );

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    filteredPhotos.length / photosPerPage
  );

  const indexOfLastPhoto =
    currentPage * photosPerPage;

  const indexOfFirstPhoto =
    indexOfLastPhoto - photosPerPage;

  const currentPhotos = filteredPhotos.slice(
    indexOfFirstPhoto,
    indexOfLastPhoto
  );

  // =====================================================
  // CATEGORY CHANGE
  // =====================================================

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  // =====================================================
  // DATE
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);

    return date.toLocaleDateString("bn-BD", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // =====================================================
  // TIME
  // =====================================================

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);

    return date.toLocaleTimeString("bn-BD", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "20px",
      }}
    >

      {/* =================================================
          PAGE HEADING
      ================================================= */}

      <div
        style={{
          borderLeft: "6px solid #d2202a",
          paddingLeft: "10px",
          marginBottom: "20px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "26px",
            color: "#222",
            fontWeight: "700",
          }}
        >
          ফটো গ্যালারি
        </h2>
      </div>

      {/* =================================================
          CATEGORY BUTTONS
      ================================================= */}

      <div
        className="photo-category-bar"
        style={{
          display: "flex",
          flexWrap: "nowrap",
          gap: "8px",
          marginBottom: "25px",
          borderBottom: "1px solid #eee",
          paddingBottom: "15px",

          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",

          scrollBehavior: "smooth",
          scrollbarWidth: "thin",
        }}
      >

        {/* ALL */}

        <button
          type="button"
          onClick={() =>
            handleCategoryChange("all")
          }
          onMouseEnter={() =>
            setHoveredCategory("all")
          }
          onMouseLeave={() =>
            setHoveredCategory(null)
          }
          style={{
            padding: "8px 16px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",

            background:
              activeCategory === "all" ||
              hoveredCategory === "all"
                ? "#d2202a"
                : "#f1f1f1",

            color:
              activeCategory === "all" ||
              hoveredCategory === "all"
                ? "#fff"
                : "#333",

            fontWeight: "bold",
            fontSize: "15px",

            transform:
              hoveredCategory === "all"
                ? "translateY(-2px)"
                : "translateY(0)",

            boxShadow:
              hoveredCategory === "all"
                ? "0 3px 8px rgba(210,32,42,0.25)"
                : "none",

            transition: "all 0.2s ease",
          }}
        >
          সর্বশেষ
        </button>

        {/* CATEGORIES */}

        {categories.map((cat) => (
          <button
            type="button"
            key={cat.id}
            onClick={() =>
              handleCategoryChange(cat.name)
            }
            onMouseEnter={() =>
              setHoveredCategory(cat.name)
            }
            onMouseLeave={() =>
              setHoveredCategory(null)
            }
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",

              background:
                activeCategory === cat.name ||
                hoveredCategory === cat.name
                  ? "#d2202a"
                  : "#f1f1f1",

              color:
                activeCategory === cat.name ||
                hoveredCategory === cat.name
                  ? "#fff"
                  : "#333",

              fontWeight: "bold",
              fontSize: "15px",

              transform:
                hoveredCategory === cat.name
                  ? "translateY(-2px)"
                  : "translateY(0)",

              boxShadow:
                hoveredCategory === cat.name
                  ? "0 3px 8px rgba(210,32,42,0.25)"
                  : "none",

              transition: "all 0.2s ease",
            }}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#777",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              border: "4px solid #eee",
              borderTop: "4px solid #d2202a",
              borderRadius: "50%",
              margin: "0 auto 15px",
              animation:
                "photoSpinner 1s linear infinite",
            }}
          />

          <div
            style={{
              fontSize: "17px",
              fontWeight: "600",
            }}
          >
            ছবি লোড হচ্ছে...
          </div>
        </div>
      ) : currentPhotos.length > 0 ? (

        <>
          {/* =================================================
              PHOTO GRID
          ================================================= */}

          <div
            className="photo-grid"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(4, minmax(0, 1fr))",
              gap: "22px",
            }}
          >

            {currentPhotos.map((photo) => (
              <Link
                key={photo.id}
                to={`/photo/${photo.id}`}
                style={{
                  textDecoration: "none",
                  color: "#222",
                  display: "block",

                  background:
                    hoveredId === photo.id
                      ? "#f7f9ff"
                      : "#fff",

                  borderRadius: "7px",
                  overflow: "hidden",

                  transition: "all 0.2s ease",

                  transform:
                    hoveredId === photo.id
                      ? "translateY(-3px)"
                      : "translateY(0)",

                  boxShadow:
                    hoveredId === photo.id
                      ? "0 5px 15px rgba(0,0,0,0.10)"
                      : "none",
                }}
                onMouseEnter={() =>
                  setHoveredId(photo.id)
                }
                onMouseLeave={() =>
                  setHoveredId(null)
                }
              >

                {/* PHOTO */}

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "4 / 3",
                    overflow: "hidden",
                    background: "#eee",
                  }}
                >
                  {photo.image && (
                    <img
                      src={photo.image}
                      alt={photo.title || "ফটো"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",

                        transform:
                          hoveredId === photo.id
                            ? "scale(1.04)"
                            : "scale(1)",

                        transition:
                          "transform 0.3s ease",
                      }}
                    />
                  )}

                  {/* PHOTO ICON */}

                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      bottom: "10px",

                      width: "34px",
                      height: "34px",

                      borderRadius: "50%",

                      background:
                        "rgba(0,0,0,0.65)",

                      color: "#fff",

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      fontSize: "15px",
                    }}
                  >
                    <FaImages />
                  </div>
                </div>

               

                {/* TITLE */}

                <h3
                  style={{
                    margin:
                      photo.category
                        ? "5px 12px 7px"
                        : "10px 12px 7px",

                    fontSize: "18px",
                    lineHeight: "1.45",
                    fontWeight: "bold",

                    color:
                      hoveredId === photo.id
                        ? "#4169E1"
                        : "#222",

                    transition: "0.2s",
                  }}
                >
                  {photo.title}
                </h3>

                {/* DATE */}

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    padding:
                      "0 12px 12px",

                    color: "#777",
                    fontSize: "12px",
                  }}
                >
                  <span>
                    <FaCalendarAlt />{" "}
                    {formatDate(
                      photo.timestamp
                    )}
                  </span>

                  <span>
                    <FaClock />{" "}
                    {formatTime(
                      photo.timestamp
                    )}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                marginTop: "30px",
                flexWrap: "wrap",
              }}
            >

              {/* PREVIOUS */}

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage(
                    (prev) => prev - 1
                  )
                }
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",

                  background:
                    currentPage === 1
                      ? "#eee"
                      : "#666",

                  color: "#fff",

                  cursor:
                    currentPage === 1
                      ? "not-allowed"
                      : "pointer",

                  fontWeight: "bold",
                }}
              >
                ‹
              </button>

              {/* PAGE NUMBERS */}

              {Array.from(
                { length: totalPages },
                (_, index) => index + 1
              ).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() =>
                    setCurrentPage(page)
                  }
                  style={{
                    minWidth: "38px",
                    padding: "8px 10px",

                    border:
                      "1px solid #ddd",

                    borderRadius: "4px",

                    background:
                      currentPage === page
                        ? "#d2202a"
                        : "#fff",

                    color:
                      currentPage === page
                        ? "#fff"
                        : "#333",

                    cursor: "pointer",
                    fontWeight: "bold",

                    transition:
                      "all 0.2s ease",
                  }}
                >
                  {page}
                </button>
              ))}

              {/* NEXT */}

              <button
                type="button"
                disabled={
                  currentPage === totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (prev) => prev + 1
                  )
                }
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",

                  background:
                    currentPage === totalPages
                      ? "#eee"
                      : "#666",

                  color: "#fff",

                  cursor:
                    currentPage === totalPages
                      ? "not-allowed"
                      : "pointer",

                  fontWeight: "bold",
                }}
              >
                ›
              </button>
            </div>
          )}
        </>
      ) : (

        /* =================================================
           NO PHOTO
        ================================================= */

        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#777",
          }}
        >
          <FaImages
            style={{
              fontSize: "38px",
              marginBottom: "12px",
              color: "#ccc",
            }}
          />

          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "5px",
            }}
          >
            কোনো ছবি পাওয়া যায়নি।
          </div>

          <div
            style={{
              fontSize: "14px",
            }}
          >
            এই ক্যাটাগরিতে বর্তমানে কোনো ছবি নেই।
          </div>
        </div>
      )}

      {/* =================================================
          RESPONSIVE CSS
      ================================================= */}

      <style>{`

        .photo-category-bar::-webkit-scrollbar {
          height: 5px;
        }

        .photo-category-bar::-webkit-scrollbar-track {
          background: transparent;
        }

        .photo-category-bar::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
        }

        .photo-category-bar::-webkit-scrollbar-thumb:hover {
          background: #999;
        }

        @media (max-width: 1100px) {
          .photo-grid {
            grid-template-columns:
              repeat(3, 1fr) !important;
          }
        }

        @media (max-width: 900px) {
          .photo-grid {
            grid-template-columns:
              repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 600px) {
          .photo-grid {
            grid-template-columns:
              1fr !important;

            gap: 20px !important;
          }
        }

        @keyframes photoSpinner {
          0% {
            transform: rotate(0deg);
          }

          100% {
            transform: rotate(360deg);
          }
        }

      `}</style>

    </div>
  );
};

export default PhotoGallery;
